import { useEffect, useRef, useState } from "react";
import { Button, Pagination, Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Select from "react-select";
import { deletePingSession, getPingSessionLocationOptions, getPingSessionSummariesPaged, sharePingSession } from "../../Api/PingSessionApi";
import { FormatDate, GetDuration } from "../../Helpers/DateTimeHelper";
import { StarRating, STAR_VALUES } from "../../Components/StarRating/StarRating";
import "../../Components/LoadingSpinner/LoadingSpinner.css";

const PAGE_SIZE = 10;
const UNRATED_VALUE = 0;
const RATING_OPTIONS = [
  ...[...STAR_VALUES].reverse().map((value) => ({ value, label: `${value} star${value > 1 ? "s" : ""}` })),
  { value: UNRATED_VALUE, label: "Unrated" },
];

export const Sessions = ({onView}) => {
  // Get the navigate function
  const navigate = useNavigate();

  const [pingSessions, setPingSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const abortControllerRef = useRef(null);

  const [selectedOrigins, setSelectedOrigins] = useState([]);
  const [selectedDestinations, setSelectedDestinations] = useState([]);
  const [selectedRatings, setSelectedRatings] = useState([]);
  const [originOptions, setOriginOptions] = useState([]);
  const [destinationOptions, setDestinationOptions] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(true);

  useEffect(() => {
    getPingSessionLocationOptions()
      .then((data) => {
        setOriginOptions((data.origins ?? []).map((origin) => ({ value: origin, label: origin })));
        setDestinationOptions((data.destinations ?? []).map((destination) => ({ value: destination, label: destination })));
      })
      .catch(() => {
        // Filters are optional; leave options empty so the table still functions.
      })
      .finally(() => setOptionsLoading(false));
  }, []);

  useEffect(() => {
    reloadPingSessions();
    return () => abortControllerRef.current?.abort();
  }, [page, selectedOrigins, selectedDestinations, selectedRatings]);

  const reloadPingSessions = () => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    getPingSessionSummariesPaged(
      page,
      PAGE_SIZE,
      selectedOrigins.map((origin) => origin.value),
      selectedDestinations.map((destination) => destination.value),
      selectedRatings.map((rating) => rating.value),
      controller.signal
    ).then((data) => {
      setPingSessions(data.items);
      setTotalPages(data.totalPages);
      setLoading(false);
    }).catch((error) => {
      if (!axios.isCancel(error)) {
        setLoading(false);
      }
    });
  };

  const handleOriginsChange = (selected) => {
    setSelectedOrigins(selected || []);
    setPage(1);
  };

  const handleDestinationsChange = (selected) => {
    setSelectedDestinations(selected || []);
    setPage(1);
  };

  const handleRatingsChange = (selected) => {
    setSelectedRatings(selected || []);
    setPage(1);
  };

  const formatRatingOptionLabel = ({ value, label }) =>
    value === UNRATED_VALUE ? label : <StarRating rating={value} readOnly size={16} />;

  const getPageNumbers = () => {
    const delta = 2;
    const pages = [];
    let previous;

    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= page - delta && i <= page + delta)) {
        if (previous !== undefined && i - previous > 1) {
          pages.push("...");
        }
        pages.push(i);
        previous = i;
      }
    }

    return pages;
  };

  const pingSessionActions = (pingSessionId) => {
    return (
      <>
        <Button
          size="sm"
          variant="success"
          onClick={() => onView(pingSessionId)}
          style={{ marginRight: "10px" }}
        >
          View
        </Button>
        <Button
          size="sm"
          variant="danger"
          style={{ marginRight: "10px" }}
          onClick={() => {
            if (window.confirm("Are you sure you want to delete this session?") == true) {
              deletePingSession(pingSessionId).then(() => {
                if (pingSessions.length === 1 && page > 1) {
                  setPage(page - 1);
                } else {
                  reloadPingSessions();
                }
              });
            }
          }}
        >
          Delete
        </Button>
        {/* <Button
          size="sm"
          variant="primary"
          onClick={() => {
            sharePingSession(pingSessionId).then((sharedSessionId) => {
              navigate("/SharedSession?sharedSessionId=" + sharedSessionId);
            });
          }}
        >
          Share
        </Button> */}
      </>
    );
  };

  return (
    <>
      <h2 style={{ marginTop: "20px" }}>Your Sessions</h2>
      <div style={{ display: "flex", gap: "16px", marginBottom: "12px" }}>
        <div style={{ flex: 1 }}>
          <label>Origin</label>
          <Select
            isMulti
            isClearable
            isLoading={optionsLoading}
            isDisabled={optionsLoading}
            closeMenuOnSelect={false}
            options={originOptions}
            value={selectedOrigins}
            onChange={handleOriginsChange}
            placeholder="All Origins"
          />
        </div>
        <div style={{ flex: 1 }}>
          <label>Destination</label>
          <Select
            isMulti
            isClearable
            isLoading={optionsLoading}
            isDisabled={optionsLoading}
            closeMenuOnSelect={false}
            options={destinationOptions}
            value={selectedDestinations}
            onChange={handleDestinationsChange}
            placeholder="All Destinations"
          />
        </div>
        <div style={{ flex: 1 }}>
          <label>Rating</label>
          <Select
            isMulti
            isClearable
            closeMenuOnSelect={false}
            options={RATING_OPTIONS}
            value={selectedRatings}
            onChange={handleRatingsChange}
            formatOptionLabel={formatRatingOptionLabel}
            placeholder="All Ratings"
          />
        </div>
      </div>
      <div style={{ position: "relative" }}>
        <Table striped bordered hover variant="">
          <thead>
            <tr>
              <th>Origin</th>
              <th>Start</th>
              <th>Destination</th>
              <th>End</th>
              <th>Duration (Minutes)</th>
              <th># Pings</th>
              <th>Rating</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pingSessions.length > 0 && pingSessions.map((pingSession) => {
              return (
                <tr>
                  <td>{pingSession.originLocationDescription}</td>
                  <td>{FormatDate(pingSession.createDate)}</td>
                  <td>{pingSession.destinationLocationDescription}</td>
                  <td>{pingSession.endDate ? FormatDate(pingSession.endDate) : "In Progress"}</td>
                  <td>{pingSession.endDate ? GetDuration(pingSession.createDate, pingSession.endDate) : "N/A"}</td>
                  <td>{pingSession.pingCount ?? "In Progress"}</td>
                  <td><StarRating rating={pingSession.rating} readOnly /></td>
                  <td>{pingSessionActions(pingSession.pingSessionId)}</td>
                </tr>
              );
            })}
            {pingSessions.length === 0 && !loading && (
              <tr>
                <td colSpan="8">No Sessions Yet</td>
              </tr>
            )}
          </tbody>
        </Table>
        {loading && (
          <div style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(128, 128, 128, 0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 5
          }}>
            <div className="loader"></div>
          </div>
        )}
      </div>
      {totalPages > 1 && (
        <Pagination>
          <Pagination.First disabled={page === 1} onClick={() => setPage(1)} />
          <Pagination.Prev disabled={page === 1} onClick={() => setPage(page - 1)} />
          {getPageNumbers().map((pageNumber, index) =>
            pageNumber === "..." ? (
              <Pagination.Ellipsis key={`ellipsis-${index}`} disabled />
            ) : (
              <Pagination.Item
                key={pageNumber}
                active={pageNumber === page}
                onClick={() => setPage(pageNumber)}
              >
                {pageNumber}
              </Pagination.Item>
            )
          )}
          <Pagination.Next disabled={page === totalPages} onClick={() => setPage(page + 1)} />
          <Pagination.Last disabled={page === totalPages} onClick={() => setPage(totalPages)} />
        </Pagination>
      )}
    </>
  );
};
